// Captures `?ref=<affiliateUid>` on landing and persists it (cookie +
// localStorage, 30-day expiry) so it survives navigation through to signup.
// `captureReferral()` runs once at module load (see App.tsx). Call
// `reportReferralConversion()` right after a signup or paid-plan subscription
// completes — it reports to affedo and clears the stored ref so it's never
// double-counted. Best-effort throughout: must never block or fail a
// signup/checkout flow.
//
// Calls affedo's own callable directly, NOT via callTeskedo() (callables.ts's
// PREFIX is teskedo-specific and currently overridden empty in .env) — same
// shared Firebase project, flat `affedo-<name>` wire name.

import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

const COOKIE_NAME = 'kw_ref';
const STORAGE_KEY = 'kw_ref';
const MAX_AGE_DAYS = 30;
const REGION = (import.meta.env.VITE_FIREBASE_REGION as string) || 'us-central1';

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Reads `?ref=` from the current URL and persists it, if present. Never
 * clears an already-stored ref (so a later page without `?ref=` doesn't wipe
 * attribution captured earlier in the session). */
export function captureReferral(): void {
  try {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (!ref) return;
    setCookie(COOKIE_NAME, ref, MAX_AGE_DAYS);
    localStorage.setItem(STORAGE_KEY, ref);
  } catch {
    /* localStorage/cookies unavailable (private mode, SSR) — non-fatal */
  }
}

export function getStoredReferral(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? getCookie(COOKIE_NAME);
  } catch {
    return null;
  }
}

export function clearStoredReferral(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  } catch {
    /* non-fatal */
  }
}

/** No-ops silently if there's no stored ref, and never throws. */
export async function reportReferralConversion(
  eventType: 'signup' | 'subscription',
  amount?: number,
): Promise<void> {
  const ref = getStoredReferral();
  if (!ref) return;
  try {
    const fn = httpsCallable(getFunctions(getApp(), REGION), 'affedo-reportAffiliateConversion');
    await fn({ ref, eventType, sourceApp: 'teskedo', amount });
    clearStoredReferral();
  } catch {
    /* best-effort — never block signup/subscribe on this */
  }
}
